/**
 * Flags Trend Chart Component
 *
 * Displays a line chart showing flagged numbers trend over time.
 */

import { useEffect, useState } from 'react';
import { cn } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import type { TrendDataPoint } from '@/types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// ============================================================================
// Types
// ============================================================================

interface ChartData extends TrendDataPoint {
  dateFormatted: string;
}

// ============================================================================
// Component
// ============================================================================

export function FlagsTrendChart() {
  const [data, setData] = useState<ChartData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const company = useAuthStore((state) => state.company);

  useEffect(() => {
    if (!company?.id) {
      setIsLoading(false);
      return;
    }

    async function fetchTrendData() {
      try {
        Logger.info(LogCategory.DATA, 'Loading trend data', 'FlagsTrendChart', { dateRange });

        const days = parseInt(dateRange);
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);

        // Fetch all phone activity in the date range
        const { data: activity, error } = await supabase
          .from('phone_activity')
          .select('activity_type, created_at')
          .eq('company_id', company!.id)
          .gte('created_at', startDate.toISOString())
          .order('created_at', { ascending: true });

        if (error) throw error;

        // Fetch current phone number counts for baseline
        const { data: phones, error: phonesError } = await supabase
          .from('phone_numbers')
          .select('flag_status, created_at')
          .eq('company_id', company!.id)
          .eq('is_archived', false);

        if (phonesError) throw phonesError;

        // Build daily data points
        const dailyMap = new Map<string, { flagged: number; clean: number }>();

        for (let i = 0; i < days; i++) {
          const d = new Date();
          d.setDate(d.getDate() - (days - 1 - i));
          const key = d.toISOString().split('T')[0];
          dailyMap.set(key, { flagged: 0, clean: 0 });
        }

        // Count activity by day
        activity?.forEach((a: any) => {
          const day = new Date(a.created_at).toISOString().split('T')[0];
          const entry = dailyMap.get(day);
          if (entry) {
            if (a.activity_type === 'flagged') entry.flagged++;
            else if (a.activity_type === 'unflagged') entry.clean++;
          }
        });

        // If no activity exists, show current phone status spread across the range
        const hasActivity = activity && activity.length > 0;
        if (!hasActivity && phones && phones.length > 0) {
          const totalFlagged = phones.filter((p: any) => p.flag_status === 'flagged').length;
          const totalClean = phones.length - totalFlagged;
          // Show flat lines at current counts
          dailyMap.forEach((entry) => {
            entry.flagged = totalFlagged;
            entry.clean = totalClean;
          });
        }

        const chartData: ChartData[] = Array.from(dailyMap.entries()).map(([date, counts]) => ({
          date,
          dateFormatted: new Date(date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          flagged: counts.flagged,
          clean: counts.clean,
          total: counts.flagged + counts.clean,
        }));

        setData(chartData);
      } catch (err) {
        Logger.error(LogCategory.DATA, 'Failed to load trend data', 'FlagsTrendChart', undefined, err);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchTrendData();
  }, [dateRange, company?.id]);

  const handleDateRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    Logger.debug(LogCategory.UI, 'Date range changed', 'FlagsTrendChart', { newRange: e.target.value });
    setDateRange(e.target.value);
  };

  return (
    <Card>
      <CardHeader
        title="Reputation Trend"
        description="Flagged vs. clean numbers over time"
        action={
          <Select
            value={dateRange}
            onChange={handleDateRangeChange}
            options={[
              { value: '7', label: 'Last 7 days' },
              { value: '14', label: 'Last 14 days' },
              { value: '30', label: 'Last 30 days' },
              { value: '60', label: 'Last 60 days' },
              { value: '90', label: 'Last 90 days' },
            ]}
            size="sm"
            fullWidth={false}
            className="w-36"
          />
        }
      />
      <CardContent>
        {isLoading ? (
          <div className="h-80 flex items-center justify-center">
            <div className="animate-pulse text-slate-400">Loading chart...</div>
          </div>
        ) : (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis
                  dataKey="dateFormatted"
                  tick={{ fill: '#64748B', fontSize: 12 }}
                  tickLine={{ stroke: '#CBD5E1' }}
                  axisLine={{ stroke: '#CBD5E1' }}
                />
                <YAxis
                  tick={{ fill: '#64748B', fontSize: 12 }}
                  tickLine={{ stroke: '#CBD5E1' }}
                  axisLine={{ stroke: '#CBD5E1' }}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                  labelStyle={{ color: '#0F172A', fontWeight: 600 }}
                  formatter={(value: number, name: string) => [
                    value.toLocaleString(),
                    name === 'clean' ? 'Clean' : name === 'flagged' ? 'Flagged' : 'Total',
                  ]}
                />
                <Legend
                  wrapperStyle={{ paddingTop: '20px' }}
                  formatter={(value) => (
                    <span className="text-sm text-slate-600 capitalize">{value}</span>
                  )}
                />
                <Line
                  type="monotone"
                  dataKey="clean"
                  stroke="#22C55E"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6, fill: '#22C55E' }}
                />
                <Line
                  type="monotone"
                  dataKey="flagged"
                  stroke="#EF4444"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6, fill: '#EF4444' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
